import { z } from "zod";
import { quantitySchema, rateSchema, uuidSchema } from "../common/primitives.js";
/**
 * Akaryakıt operasyonu ayarları (Prompt 12). Satır ŞİRKET düzeyindedir; `stationId` doluysa o istasyon için
 * geçersiz kılmadır. Hiçbir eşik koda gömülmez (AGENTS.md §3.12): tümü bu tablodan okunur ve seed'de
 * `needsVerification = true` ("doğrulanmalı") ile yazılır.
 */
export const TEMPERATURE_CORRECTION_STRATEGY_CODES = ["SIMPLE_COEFFICIENT"];
export const temperatureStrategySchema = z.enum(TEMPERATURE_CORRECTION_STRATEGY_CODES);
export const TEMPERATURE_STRATEGY_LABELS = {
    SIMPLE_COEFFICIENT: "Basit katsayı (V15 = V × (1 − β × (T − 15)))",
};
export const DELIVERY_COMPARE_BASES = ["AMBIENT", "REFERENCE_15C"];
export const deliveryCompareBasisSchema = z.enum(DELIVERY_COMPARE_BASES);
export const DELIVERY_COMPARE_BASIS_LABELS = {
    AMBIENT: "Ortam sıcaklığındaki hacim",
    REFERENCE_15C: "15 °C'ye indirgenmiş hacim",
};
export const FUEL_COST_SCOPES = ["STATION_PRODUCT", "COMPANY_PRODUCT"];
export const fuelCostScopeSchema = z.enum(FUEL_COST_SCOPES);
export const FUEL_COST_SCOPE_LABELS = {
    STATION_PRODUCT: "İstasyon × ürün",
    COMPANY_PRODUCT: "Şirket × ürün",
};
/**
 * Islak stok mutabakatı kural eşikleri. Alanlar `apps/api/src/fuel/domain/reconciliation.ts >
 * ReconciliationThresholds` ile BİREBİR aynıdır (birim testi bunu doğrular); ondalıklar dizgedir (float yok).
 */
export const reconciliationThresholdsSchema = z.object({
    dailyVariance: z
        .object({
        minimumL: quantitySchema.describe("Altında kural hiç çalışmayan mutlak fark (L)"),
        watchPercent: rateSchema,
        suspiciousPercent: rateSchema,
        criticalPercent: rateSchema,
    })
        .describe("Tek dönem farkı: mutlak alt sınırı aşan fark, satışa oranına göre kademelenir"),
    cumulativeTrend: z
        .object({
        periods: z.number().int().min(2).max(90).describe("Eğilim penceresi (dönem sayısı)"),
        minimumNegativePeriods: z.number().int().min(1).max(90),
        suspiciousPercent: rateSchema,
        criticalPercent: rateSchema,
    })
        .describe("Son N dönemin en az M'si negatifse ve kümülatif kayıp satışın %X'ini aşıyorsa"),
    idleLevelDrop: z
        .object({
        suspiciousLPerHour: quantitySchema,
        criticalLPerHour: quantitySchema,
        minimumDropL: quantitySchema
            .optional()
            .describe("Gürültü tabanı (L): aralıktaki toplam düşüş bunun altındaysa değerlendirilmez; yoksa 10 L"),
    })
        .describe("Satış YOKKEN seviye düşüş hızı (L/saat): sızıntı şüphesi"),
    waterRise: z
        .object({ watchMm: quantitySchema, criticalMm: quantitySchema })
        .describe("Dönem içinde su seviyesi artışı (mm)"),
    salesCheck: z
        .object({ watchL: quantitySchema, suspiciousL: quantitySchema })
        .describe("Endeks satışı ↔ işlem satışı farkı (L): veri kaybı işareti"),
});
/**
 * Varsayılan eşikler. Bu sayıların yasal bir dayanağı YOKTUR; sektör kabulüne göre seçilmiştir ve kiracıya
 * "doğrulanmalı" olarak sunulur (docs/domain/regulatory-open-questions.md).
 */
export const DEFAULT_RECONCILIATION_THRESHOLDS = {
    dailyVariance: { minimumL: "30", watchPercent: "0.3", suspiciousPercent: "0.6", criticalPercent: "1.5" },
    cumulativeTrend: {
        periods: 7,
        minimumNegativePeriods: 6,
        suspiciousPercent: "0.25",
        criticalPercent: "0.6",
    },
    idleLevelDrop: { suspiciousLPerHour: "0.3", criticalLPerHour: "2", minimumDropL: "10" },
    waterRise: { watchMm: "5", criticalMm: "20" },
    salesCheck: { watchL: "5", suspiciousL: "50" },
};
/** Tankerin kompartıman boyutları (L) — sipariş önerisi bu boyutlara yuvarlanır. */
export const compartmentSizesSchema = z
    .array(quantitySchema)
    .min(1)
    .max(12)
    .describe("Tanker kompartıman boyutları (L, büyükten küçüğe)");
const settingsFields = {
    temperatureStrategy: temperatureStrategySchema,
    deliveryCompareBasis: deliveryCompareBasisSchema,
    deliveryTolerancePerMille: rateSchema.describe("Dolum farkı toleransı (‰)"),
    deliveryToleranceMinL: quantitySchema.describe("Dolum farkı toleransının mutlak alt sınırı (L)"),
    settleMinutes: z.number().int().min(0).max(240).describe("Boşaltma sonrası oturma süresi (dk)"),
    normalLossPercent: rateSchema.describe("Normal fire sınırı (satışın yüzdesi)"),
    reconciliationThresholds: reconciliationThresholdsSchema,
    totalizerRolloverWindowL: quantitySchema.describe("Endeks taşması sayılma penceresi (L)"),
    mechanicalMismatchL: quantitySchema.describe("Elektronik ↔ mekanik endeks fark eşiği (L)"),
    priceGraceSeconds: z
        .number()
        .int()
        .min(0)
        .max(3600)
        .describe("Fiyat yürürlüğünden sonra eski fiyata izin verilen süre (sn)"),
    forecastWeeks: z.number().int().min(1).max(26).describe("Tüketim profilinde kullanılan hafta sayısı"),
    leadTimeHours: z.number().int().min(1).max(336).describe("Sipariş teslim süresi (saat)"),
    minimumStockDays: rateSchema.describe("Hedef asgari stok (gün)"),
    compartmentSizesL: compartmentSizesSchema,
};
/** Yalnızca ŞİRKET satırında değiştirilebilen alanlar; istasyon geçersiz kılmasında gönderilemez. */
export const COMPANY_ONLY_FUEL_SETTINGS = ["costScope", "postDailySalesSummary"];
export const fuelSettingsSchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.nullable().describe("Dolu ise bu istasyona özel geçersiz kılma"),
    /** true → kayıt YOKTUR; dönen değerler ürün varsayılanıdır (ilk PUT satırı oluşturur). */
    isDefault: z.boolean(),
    ...settingsFields,
    costScope: fuelCostScopeSchema.describe("Maliyet kapsamı — yalnızca ŞİRKET satırında"),
    postDailySalesSummary: z
        .boolean()
        .describe("Gün sonu satış özeti fişi kesilsin mi — yalnızca ŞİRKET satırında"),
    needsVerification: z.boolean().describe("Değerler mali müşavir / işletme tarafından doğrulanmalı"),
    version: z.number().int().min(0).describe("İyimser kilit sürümü; 0 = henüz kaydedilmemiş"),
});
/**
 * PUT ile TAMAMI değiştirilir (kısmi güncelleme yok) — bu yüzden adı `save…`'dir: `update…` adlı şemalar
 * `common/patch.test.ts` korumasına girer ve varsayılan içeren alanlarda kırılır.
 */
export const saveFuelSettingsSchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.nullable().optional().describe("Verilirse istasyon geçersiz kılması yazılır"),
    ...settingsFields,
    costScope: fuelCostScopeSchema.optional(),
    postDailySalesSummary: z.boolean().optional(),
    needsVerification: z.boolean().optional(),
    version: z.number().int().min(0).describe("Okunan sürüm; 0 = kayıt yoktu"),
});
export const fuelSettingsQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional().describe("Verilirse istasyonun ETKİN ayarı (yoksa şirket satırı) döner"),
});
//# sourceMappingURL=settings.js.map